import type { CSSProperties, ReactNode } from "react";

type BorderBeamProps = {
  children: ReactNode;
  color?: string;
  durationSec?: number;
  className?: string;
  style?: CSSProperties;
};

/**
 * Spinning border-light effect around a CTA button.
 *
 * Works on all browsers (including iOS Safari) without CSS @property:
 * - The container uses WebkitMask to reveal ONLY the 1px padding ring,
 *   hiding the content-box beneath (standard mask-composite:exclude trick).
 * - A 200% × 200% child with a static conic-gradient is rotated via
 *   transform:rotate() - zero Houdini required.
 */
export function BorderBeam({ children, color = "#BEF264", durationSec = 4, className, style }: BorderBeamProps) {
  return (
    <div className={className} style={{ position: "relative", display: "inline-block", ...style }}>
      <div
        aria-hidden="true"
        style={{
          position: "absolute",
          inset: 0,
          borderRadius: "inherit",
          padding: 1,
          // Reveal only the 1-px border ring, hide the content box
          WebkitMask: "linear-gradient(#000 0 0) content-box, linear-gradient(#000 0 0)",
          WebkitMaskComposite: "xor",
          maskComposite: "exclude",
          overflow: "hidden",
          pointerEvents: "none",
        }}
      >
        {/* Oversized square centered on the button, rotated continuously */}
        <div
          style={{
            position: "absolute",
            inset: "-50%",
            background: `conic-gradient(from 0deg, transparent 75%, ${color} 82%, transparent 89%)`,
            animation: `border-beam-rotate ${durationSec}s linear infinite`,
          }}
        />
      </div>
      <style>{`
        @keyframes border-beam-rotate {
          from { transform: rotate(0deg); }
          to   { transform: rotate(360deg); }
        }
      `}</style>
      {children}
    </div>
  );
}
